import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import CashflowPhasingDialog from './CashflowPhasingDialog';

import Modal from 'CommonComponents/Modal';

import useModal from 'Hooks/useModal';

/**
 * CashflowPhasingDialogApp
 *
 * One React root owning the uncommitted cashflow phasing dialog on the
 * budget-summary cashflow grid. The expenditure table (plain JS, not bundled)
 * opens it by dispatching a window CustomEvent `cashflowphasing:open`:
 *
 *   {
 *     mode:  'single' | 'multi',
 *     lines: [{ expense_id, name, uncommitted, uncommitted_by_type, status, types }],
 *     periods: ['MM-YYYY', …],   // optional; the grid's month columns
 *   }
 *
 * After a save it dispatches `cashflowphasing:saved` with `{ updated, skipped }`
 * so the table can clear its selection and re-fetch.
 */
const CashflowPhasingDialogApp = ({ showUrl, saveUrl, defaultMonth }) => {
    const modal = useModal();
    const [opening, setOpening] = useState(null);
    const openCount = useRef(0);

    const handleOpen = useCallback(
        (e) => {
            const detail = e.detail || {};
            if (!Array.isArray(detail.lines) || detail.lines.length === 0) return;
            openCount.current += 1;
            setOpening({
                key: openCount.current,
                mode: detail.mode === 'single' && detail.lines.length === 1 ? 'single' : 'multi',
                lines: detail.lines,
                periods: Array.isArray(detail.periods) ? detail.periods : [],
            });
            modal.show();
        },
        [modal.show],
    );

    useEffect(() => {
        window.addEventListener('cashflowphasing:open', handleOpen);
        return () => window.removeEventListener('cashflowphasing:open', handleOpen);
    }, [handleOpen]);

    const handleSaved = useCallback((result) => {
        window.dispatchEvent(new CustomEvent('cashflowphasing:saved', { detail: result }));
    }, []);

    return (
        <Modal id="cashflow-phasing-modal" modal={modal} innerContainerClasses="tw-max-w-2xl">
            {opening && (
                <CashflowPhasingDialog
                    key={opening.key}
                    opening={opening}
                    showUrl={showUrl}
                    saveUrl={saveUrl}
                    defaultMonth={defaultMonth}
                    periods={opening.periods}
                    onClose={modal.hide}
                    onSaved={handleSaved}
                />
            )}
        </Modal>
    );
};

const rootEl = document.getElementById('CashflowPhasingDialogApp');
if (rootEl) {
    ReactDOM.render(
        <App>
            <CashflowPhasingDialogApp
                showUrl={rootEl.getAttribute('data-show-url')}
                saveUrl={rootEl.getAttribute('data-save-url')}
                defaultMonth={rootEl.getAttribute('data-default-month') || null}
            />
        </App>,
        rootEl,
    );
}
